import axiosInstance from '../utils/axiosConfig';

/**
 * Service for handling Stock/Product-related API calls
 * Optimized for invoice line item integration
 */

export const stockService = {

    // --- Bulk product import (CSV) --------------------------------------
    // Preview-then-confirm, same shape as the CRM credit upload: a supplier
    // catalogue is hundreds of rows and nobody can check that by reading a
    // success toast.

    listCsvFormats: async () => {
        const res = await axiosInstance.get('/api/stock/stock-items/csv-formats/');
        return res.data;
    },

    // NOTE: the query parameter is ``product_format``, not ``format`` -- DRF
    // reserves ``format`` for content negotiation and would 404 on it.
    csvTemplateUrl: (formatKey) => (
        `/api/stock/stock-items/csv-template/?product_format=${encodeURIComponent(formatKey)}`
    ),

    downloadCsvTemplate: async (formatKey) => {
        const res = await axiosInstance.get(stockService.csvTemplateUrl(formatKey), {
            responseType: 'blob',
        });
        return res.data;
    },

    // --- HMS lube list (their own sheet, the COMPLETE list every time) -----
    // Rules live server-side in stock/services/supplier_list.py; the screen
    // only shows the piles and sends back the uploader's answers.

    reviewSupplierList: async (file, answers = {}) => {
        const form = new FormData();
        form.append('file', file);
        form.append('answers', JSON.stringify(answers));
        const res = await axiosInstance.post(
            '/api/stock/stock-items/supplier-list/review/', form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return res.data;
    },

    applySupplierList: async (file, answers = {}, { confirmLargeRetire = false } = {}) => {
        const form = new FormData();
        form.append('file', file);
        form.append('answers', JSON.stringify(answers));
        if (confirmLargeRetire) form.append('confirm_large_retire', 'true');
        const res = await axiosInstance.post(
            '/api/stock/stock-items/supplier-list/apply/', form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return res.data;
    },

    supplierListHistory: async () => {
        const res = await axiosInstance.get('/api/stock/stock-items/supplier-list/history/');
        return res.data;
    },

    restoreSupplierList: async (uploadId, { preview = false, answers = {}, confirmLargeRetire = false } = {}) => {
        const res = await axiosInstance.post('/api/stock/stock-items/supplier-list/restore/', {
            upload_id: uploadId,
            preview: preview ? 'true' : '',
            answers: JSON.stringify(answers),
            confirm_large_retire: confirmLargeRetire ? 'true' : '',
        });
        return res.data;
    },

    importProductsCsv: async (file, formatKey, { preview = false } = {}) => {
        const form = new FormData();
        form.append('file', file);
        form.append('format', formatKey);
        if (preview) form.append('preview', 'true');
        const res = await axiosInstance.post(
            '/api/stock/stock-items/csv-import/',
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return res.data;
    },
    /**
     * Search product variants for invoice line items
     * Returns flattened variant data optimized for auto-population
     * 
     * @param {string} searchTerm - Search query (min 2 characters)
     * @param {string} invoiceType - Optional invoice type context (OUTGOING, PROFORMA, INCOMING, PURCHASE_ORDER)
     * @returns {Promise<Array>} - Array of variant objects
     */
    /**
     * Why a product search came back empty, in words the operator can act on.
     *
     * Every picker used to render ANY failure as "No products found." — a 403
     * (the endpoint was gated on a flag Invoicing doesn't grant) read exactly
     * like an empty catalogue, so the real cause never reached the user
     * (invoice-product-search-silent-drops, 2026-08-19). Only a successful,
     * genuinely empty response may say "No products found".
     */
    productSearchErrorMessage: (error) => {
        const status = error?.response?.status;
        if (status === 401) return 'Your session expired — sign in again to search products.';
        if (status === 403) return "You don't have access to the product list. Ask an administrator for Financials, Cargo or Bunkers access.";
        if (status === 404) return 'Product search is unavailable (endpoint not found). Contact support.';
        if (status >= 500) return 'Product search failed on the server. Try again in a moment.';
        return 'Product search failed — check your connection and try again.';
    },

    /**
     * Every deal this product variant was bought and sold in.
     *
     * Deliberately NOT called when the product list renders: that screen can
     * list hundreds of variants, and one request each would hammer the server
     * for panels nobody opened. The Movement panel asks only when it is opened.
     *
     * Errors are re-thrown untouched so the panel can read the status with
     * `describeLoadFailure` -- a swallowed failure here would reach the screen
     * as an empty list, which reads as "never traded" and is a different and
     * wrong statement.
     */
    getVariantMovement: async (variantId) => {
        const response = await axiosInstance.get(
            `/api/stock/variants/${variantId}/movement/`,
        );
        return response.data;
    },

    // `includeServices` is for the invoice/note/PO line grids ONLY: they apply
    // a pick through applyPickedResultToLine, which never files a service as a
    // product. Every other host stores a product link and must not ask.
    searchVariantsForInvoice: async (searchTerm, invoiceType = null, { includeServices = false } = {}) => {
        try {
            if (!searchTerm || searchTerm.trim().length < 2) {
                return [];
            }

            const params = new URLSearchParams();
            params.append('q', searchTerm.trim());

            if (invoiceType) {
                params.append('invoice_type', invoiceType);
            }
            if (includeServices) {
                params.append('include_services', '1');
            }

            const url = `/api/stock/variants/search/?${params.toString()}`;
            const response = await axiosInstance.get(url);
            
            console.log(`Stock variant search for "${searchTerm}" (${invoiceType || 'any'}):`, response.data.results?.length || 0, 'results');
            return response.data.results || [];
        } catch (error) {
            console.error('Error searching stock variants:', error);
            throw error;
        }
    },

    /**
     * Get full details of a specific variant by ID
     * 
     * @param {string} variantId - UUID of the variant
     * @returns {Promise<Object>} - Variant object with full details
     */
    getVariantById: async (variantId) => {
        try {
            const response = await axiosInstance.get(`/api/stock/stock-variants/${variantId}/`);
            console.log('Fetched variant details:', variantId);
            return response.data;
        } catch (error) {
            console.error('Error fetching variant details:', error);
            throw error;
        }
    },

    /**
     * Search stock items (base level)
     * 
     * @param {Object} options - Search options
     * @param {string} options.q - Search query
     * @param {string} options.product_type - Filter by product type
     * @param {string} options.country - Filter by country
     * @param {string} options.supplier - Filter by supplier
     * @returns {Promise<Array>} - Array of stock item results
     */
    searchStockItems: async (options = {}) => {
        try {
            const params = new URLSearchParams();
            
            if (options.q) params.append('q', options.q);
            if (options.product_type) params.append('product_type', options.product_type);
            if (options.country) params.append('country', options.country);
            if (options.supplier) params.append('supplier', options.supplier);

            const url = `/api/stock/search/?${params.toString()}`;
            const response = await axiosInstance.get(url);
            
            console.log('Stock item search:', response.data.results?.length || 0, 'results');
            return response.data.results || [];
        } catch (error) {
            console.error('Error searching stock items:', error);
            throw error;
        }
    },

    /**
     * Get list of available product types
     * 
     * @returns {Promise<Array>} - Array of {value, label} objects
     */
    getProductTypes: async () => {
        try {
            const response = await axiosInstance.get('/api/stock/product-types/');
            console.log('Fetched product types:', response.data.length);
            return response.data;
        } catch (error) {
            console.error('Error fetching product types:', error);
            throw error;
        }
    },

    /**
     * Get list of countries with stock items
     * 
     * @returns {Promise<Array>} - Array of country names
     */
    getCountries: async () => {
        try {
            const response = await axiosInstance.get('/api/stock/countries/');
            console.log('Fetched countries:', response.data.length);
            return response.data;
        } catch (error) {
            console.error('Error fetching countries:', error);
            throw error;
        }
    },

    /**
     * Format variant for display in dropdown
     * Creates a user-friendly display string from variant data
     * 
     * @param {Object} variant - Variant object from API
     * @returns {string} - Formatted display string
     */
    formatVariantForDisplay: (variant) => {
        if (!variant) return '';
        
        // Use the pre-computed display_name from backend
        return variant.display_name || 
               `${variant.base_item_name || 'Unknown'} - ${variant.shortname || 'Variant'}`;
    },

    // ── Service CRUD ─────────────────────────────────────────────

    fetchServices: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/api/stock/services/', { params });
            return response.data.results || response.data;
        } catch (error) {
            console.error('Error fetching services:', error);
            throw error;
        }
    },

    fetchServiceTypes: async () => {
        try {
            const response = await axiosInstance.get('/api/stock/services/service_types/');
            return response.data;
        } catch (error) {
            console.error('Error fetching service types:', error);
            throw error;
        }
    },

    createService: async (data) => {
        try {
            const response = await axiosInstance.post('/api/stock/services/', data);
            return response.data;
        } catch (error) {
            console.error('Error creating service:', error);
            throw error;
        }
    },

    updateService: async (id, data) => {
        try {
            const response = await axiosInstance.patch(`/api/stock/services/${id}/`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating service:', error);
            throw error;
        }
    },
};

export default stockService;







